import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api } from '../api/client';
import {
  Button,
  Card,
  Input,
  Select,
  StatCard,
  Toggle,
} from '@/admin-design/_common';
import type { StatCardTone } from '@/admin-design/_common';

type StatusSyncJobType =
  | 'product_state_sync'
  | 'naver_pull_state'
  | 'supplier_pull_state'
  | 'supplier_apply_state';

type ManualJobType =
  | 'crawl'
  | 'register'
  | 'cleanup_duplicates'
  | 'seo_optimize'
  | 'track_orders'
  | 'reprice'
  | 'price_bulk_adjust'
  | 'expose_floor_enforce';

type RunnableJobType = ManualJobType | StatusSyncJobType;

const JOB_OPTIONS: Array<{ value: ManualJobType; label: string; description: string }> = [
  { value: 'crawl', label: '도매처 크롤', description: '도매처 상품을 가져옵니다.' },
  { value: 'register', label: '네이버 상품 등록', description: '미등록 상품을 네이버에 등록합니다.' },
  { value: 'cleanup_duplicates', label: '중복 상품 정리', description: '중복 판매자상품코드를 점검합니다.' },
  { value: 'seo_optimize', label: 'SEO 최적화', description: '등록 상품의 SEO 정보를 보강합니다.' },
  { value: 'track_orders', label: '발주 추적', description: '도매처 발주 상태를 가져옵니다.' },
  { value: 'reprice', label: '리프라이스', description: '경쟁 가격을 기준으로 가격을 계산합니다.' },
  { value: 'price_bulk_adjust', label: '일괄 가격 조정', description: '설정한 규칙으로 판매가를 조정합니다.' },
  {
    value: 'expose_floor_enforce',
    label: '노출최저가 강제',
    description: '도매처 노출최저가보다 싸게 걸린 상품을 노출최저가로 끌어올립니다.',
  },
];

function parsePositiveInteger(value: string): number | null | undefined {
  const trimmed = value.trim();
  if (!trimmed) return undefined;

  const parsed = Number(trimmed);
  return Number.isFinite(parsed) && Number.isInteger(parsed) && parsed > 0 ? parsed : null;
}

export default function Dashboard() {
  const stats = useQuery({ queryKey: ['stats'], queryFn: () => api.stats() });
  const qc = useQueryClient();

  const providersQ = useQuery({ queryKey: ['providers'], queryFn: () => api.providers() });
  const availableProviders: string[] = Array.isArray(providersQ.data) ? providersQ.data : [];
  const [provider, setProvider] = useState('wsmon');
  const [maxPages, setMaxPages] = useState('');
  const [concurrency, setConcurrency] = useState('4');
  const [registerLimit, setRegisterLimit] = useState('10');
  const [manualJob, setManualJob] = useState<ManualJobType>('crawl');
  const [exposeFloorDryRun, setExposeFloorDryRun] = useState(true);
  const [cleanupDryRun, setCleanupDryRun] = useState(true);
  const [bulkMode, setBulkMode] = useState<'margin' | 'pct' | 'flat' | 'expose'>('margin');
  const [bulkValue, setBulkValue] = useState('1.5');
  const [bulkScope, setBulkScope] = useState<'all' | 'margin_only' | 'product_ids'>('all');
  const [bulkProductIDs, setBulkProductIDs] = useState('');
  const [bulkDryRun, setBulkDryRun] = useState(true);
  const [queuedJob, setQueuedJob] = useState<{ id: number; type: RunnableJobType } | null>(null);
  const [manualJobError, setManualJobError] = useState<string | null>(null);

  const runJob = useMutation<{ id: number }, Error, { type: RunnableJobType; payload: Record<string, unknown> }>({
    mutationFn: ({ type, payload }) => api.createJob(type, payload) as Promise<{ id: number }>,
    onMutate: () => setQueuedJob(null),
    onSuccess: ({ id }, variables) => {
      setQueuedJob({ id, type: variables.type });
      qc.invalidateQueries({ queryKey: ['jobs'] });
    },
  });

  const cards: { label: string; value: number; tone?: StatCardTone }[] = [
    { label: '전체 상품', value: stats.data?.total ?? 0 },
    { label: '싱크 완료', value: stats.data?.synced ?? 0, tone: 'success' },
    { label: '싱크 대기', value: stats.data?.pending ?? 0, tone: 'warning' },
    { label: '에러', value: stats.data?.errored ?? 0, tone: 'destructive' },
    { label: '품절', value: stats.data?.sold_out ?? 0, tone: 'muted' },
    { label: '작업 대기', value: stats.data?.jobs_pending ?? 0 },
    { label: '작업 실행중', value: stats.data?.jobs_running ?? 0 },
  ];

  const providerOptions = (availableProviders.length > 0 ? availableProviders : ['wsmon']).map((p) => ({
    label: p,
    value: p,
  }));
  const selectedJob = JOB_OPTIONS.find((job) => job.value === manualJob) ?? JOB_OPTIONS[0];

  function registerPayloadOrNull(): Record<string, unknown> | null {
    const limit = parseInt(registerLimit, 10);
    if (Number.isNaN(limit)) return {};
    if (limit === 0) {
      if (!window.confirm('등록할 상품 수가 0입니다.\n미등록/실패 상태의 모든 상품을 등록합니다. 계속할까요?')) {
        return null;
      }
      return { limit: 0 };
    }
    return limit > 0 ? { limit } : {};
  }

  function bulkPayloadOrNull(): Record<string, unknown> | null {
    const value = Number(bulkValue);
    if (!Number.isFinite(value)) {
      setManualJobError('일괄 가격 조정 값은 유한한 숫자여야 합니다.');
      return null;
    }

    const payload: Record<string, unknown> = {
      mode: bulkMode,
      value,
      scope: bulkScope,
      dry_run: bulkDryRun,
    };
    if (bulkScope === 'product_ids') {
      const productIDs = bulkProductIDs
        .split(',')
        .map((id) => id.trim())
        .filter(Boolean)
        .map(Number);
      if (productIDs.length === 0 || productIDs.some((id) => !Number.isInteger(id))) {
        setManualJobError('적용할 상품 ID를 쉼표로 구분한 정수로 입력하세요.');
        return null;
      }
      payload.product_ids = productIDs;
    }
    return payload;
  }

  function payloadForManualJob(): Record<string, unknown> | null {
    if (manualJob === 'crawl') {
      const maxPagesValue = parsePositiveInteger(maxPages);
      if (maxPagesValue === null) {
        setManualJobError('크롤할 페이지 수는 1 이상의 정수여야 합니다. 비우면 전체를 크롤합니다.');
        return null;
      }

      const concurrencyValue = parsePositiveInteger(concurrency);
      if (concurrencyValue === null || (concurrencyValue !== undefined && concurrencyValue > 16)) {
        setManualJobError('동시성은 1에서 16 사이의 정수여야 합니다.');
        return null;
      }

      return {
        provider,
        ...(maxPagesValue === undefined ? {} : { max_pages: maxPagesValue }),
        ...(concurrencyValue === undefined ? {} : { concurrency: concurrencyValue }),
      };
    }
    if (manualJob === 'register') return registerPayloadOrNull();
    if (manualJob === 'cleanup_duplicates') return { dry_run: cleanupDryRun };
    if (manualJob === 'expose_floor_enforce') return { dry_run: exposeFloorDryRun };
    if (manualJob === 'price_bulk_adjust') return bulkPayloadOrNull();
    return {};
  }

  function runManualJob() {
    setQueuedJob(null);
    setManualJobError(null);
    const payload = payloadForManualJob();
    if (!payload) return;
    if (
      manualJob === 'seo_optimize' &&
      !window.confirm('SEO 최적화는 최대 9,999개 상품에 대해 LLM 작업을 실행할 수 있습니다. 계속할까요?')
    ) {
      return;
    }
    if (
      manualJob === 'cleanup_duplicates' &&
      !cleanupDryRun &&
      !window.confirm('중복 상품 정리는 중복된 네이버 판매 목록을 판매중지(SUSPEND)합니다. 계속할까요?')
    ) {
      return;
    }
    if (
      manualJob === 'expose_floor_enforce' &&
      !exposeFloorDryRun &&
      !window.confirm(
        '노출최저가보다 싸게 걸린 상품의 네이버 판매가를 노출최저가로 올립니다.\n인상 상한(설정값)을 넘는 건은 적용하지 않고 경고만 남깁니다. 계속할까요?',
      )
    ) {
      return;
    }
    if (
      manualJob === 'price_bulk_adjust' &&
      !bulkDryRun &&
      !window.confirm(
        `일괄 가격 조정을 실제 적용합니다.\n모드: ${bulkMode}\n값: ${bulkValue}\n범위: ${bulkScope}\n계속할까요?`,
      )
    ) {
      return;
    }
    runJob.mutate({ type: manualJob, payload });
  }

  function runStatusSync(type: StatusSyncJobType) {
    setManualJobError(null);
    if (
      (type === 'supplier_apply_state' || type === 'product_state_sync') &&
      !window.confirm('DB의 도매처 상태를 네이버 판매 및 전시 상태에 적용합니다. 계속할까요?')
    ) {
      return;
    }
    runJob.mutate({ type, payload: {} });
  }

  function renderQueuedJobMessage(type: RunnableJobType) {
    if (!queuedJob || queuedJob.type !== type) return null;

    return (
      <p className="text-sm text-success">
        작업 #{queuedJob.id}이 큐에 추가되었습니다. <Link to={`/jobs?job_id=${queuedJob.id}`}>작업 보기</Link>
      </p>
    );
  }

  function renderJobError(type: RunnableJobType) {
    if (!runJob.isError || runJob.variables?.type !== type) return null;

    return <p className="text-sm text-destructive">실패: {runJob.error.message}</p>;
  }

  function renderManualPayload() {
    if (manualJob === 'crawl') {
      return (
        <div className="flex flex-col sm:flex-row items-start sm:items-end gap-3 flex-wrap">
          <div>
            <label className="block text-xs text-muted-foreground mb-1">도매처</label>
            <Select value={provider} onValueChange={setProvider} options={providerOptions} className="w-32" />
          </div>
          <div>
            <label className="block text-xs text-muted-foreground mb-1">크롤할 페이지 수 (비우면 전체)</label>
            <Input
              type="number"
              min={1}
              className="w-32"
              placeholder="전체"
              value={maxPages}
              onChange={(e) => setMaxPages(e.target.value)}
            />
          </div>
          <div>
            <label className="block text-xs text-muted-foreground mb-1">동시성 (1-16)</label>
            <Input
              type="number"
              min={1}
              max={16}
              className="w-24"
              value={concurrency}
              onChange={(e) => setConcurrency(e.target.value)}
            />
          </div>
        </div>
      );
    }

    if (manualJob === 'register') {
      return (
        <div>
          <label className="block text-xs text-muted-foreground mb-1">
            등록할 상품 수 <span className="text-muted-foreground">(0 = 전체)</span>
          </label>
          <Input
            type="number"
            min={0}
            className="w-44"
            value={registerLimit}
            onChange={(e) => setRegisterLimit(e.target.value)}
          />
        </div>
      );
    }

    if (manualJob === 'cleanup_duplicates') {
      return (
        <div className="flex items-center gap-3">
          <Toggle checked={cleanupDryRun} onChange={setCleanupDryRun} aria-label="중복 상품 정리 미리 실행" />
          <div>
            <p className="text-sm font-medium">미리 실행</p>
            <p className="text-xs text-muted-foreground">정리 대상만 확인하고 변경하지 않습니다.</p>
          </div>
        </div>
      );
    }

    if (manualJob === 'expose_floor_enforce') {
      return (
        <div className="flex items-center gap-3">
          <Toggle
            checked={exposeFloorDryRun}
            onChange={setExposeFloorDryRun}
            aria-label="노출최저가 강제 미리 실행"
          />
          <div>
            <p className="text-sm font-medium">미리 실행</p>
            <p className="text-xs text-muted-foreground">
              올릴 대상만 확인하고 네이버에 반영하지 않습니다.
            </p>
          </div>
        </div>
      );
    }

    if (manualJob === 'price_bulk_adjust') {
      return (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="block text-xs text-muted-foreground mb-1">조정 모드</label>
            <Select
              value={bulkMode}
              onValueChange={(value) => setBulkMode(value as typeof bulkMode)}
              options={[
                { value: 'margin', label: 'margin (도매가 마진율)' },
                { value: 'pct', label: 'pct (정률 조정)' },
                { value: 'flat', label: 'flat (정액 가감)' },
                { value: 'expose', label: 'expose (도매최저노출가)' },
              ]}
            />
          </div>
          <div>
            <label className="block text-xs text-muted-foreground mb-1">값</label>
            <Input type="number" value={bulkValue} onChange={(e) => setBulkValue(e.target.value)} />
          </div>
          <div>
            <label className="block text-xs text-muted-foreground mb-1">적용 범위</label>
            <Select
              value={bulkScope}
              onValueChange={(value) => setBulkScope(value as typeof bulkScope)}
              options={[
                { value: 'all', label: '전체 상품' },
                { value: 'margin_only', label: '마진가 상품만' },
                { value: 'product_ids', label: '상품 ID 지정' },
              ]}
            />
          </div>
          {bulkScope === 'product_ids' && (
            <div>
              <label className="block text-xs text-muted-foreground mb-1">상품 ID (쉼표 구분)</label>
              <Input
                value={bulkProductIDs}
                placeholder="예: 12, 34, 56"
                onChange={(e) => setBulkProductIDs(e.target.value)}
              />
            </div>
          )}
          <div className="flex items-center gap-3 sm:col-span-2">
            <Toggle checked={bulkDryRun} onChange={setBulkDryRun} aria-label="일괄 가격 조정 미리 실행" />
            <div>
              <p className="text-sm font-medium">미리 실행</p>
              <p className="text-xs text-muted-foreground">끄면 네이버 판매가를 실제로 변경합니다.</p>
            </div>
          </div>
        </div>
      );
    }

    return null;
  }

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold">대시보드</h1>

      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
        {cards.map((c) => (
          <StatCard key={c.label} label={c.label} value={c.value} tone={c.tone} />
        ))}
      </div>

      <Card>
        <Card.Header>
          <Card.Title>상태 동기화</Card.Title>
          <Card.Description>스토어 상태를 최신 값으로 맞춥니다.</Card.Description>
        </Card.Header>
        <Card.Body className="space-y-4">
          <div className="space-y-3 rounded-lg border border-primary/40 bg-primary/5 p-4">
            <Button
              variant="primary"
              onClick={() => runStatusSync('product_state_sync')}
              loading={runJob.isPending}
            >
              전체 상태 동기화 (3단계 순차)
            </Button>
            <p className="text-sm text-muted-foreground">
              아래 3단계를 네이버 → DB, 도매처 → DB, DB → 네이버 순서로 한 번에 실행합니다. 매일 이
              작업이 자동 실행되며, 아래 개별 버튼은 한 단계만 다시 돌릴 때 사용합니다.
            </p>
          </div>

          <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
            <div className="space-y-3 rounded-lg border border-border p-4">
              <Button
                variant="secondary"
                onClick={() => runStatusSync('naver_pull_state')}
                loading={runJob.isPending}
              >
                네이버 상품 상태 → DB
              </Button>
              <p className="text-sm text-muted-foreground">
                네이버의 판매 상태, 전시 상태, 재고, 가격을 DB에만 가져옵니다.
              </p>
            </div>
            <div className="space-y-3 rounded-lg border border-border p-4">
              <Button
                variant="secondary"
                onClick={() => runStatusSync('supplier_pull_state')}
                loading={runJob.isPending}
              >
                도매처 상품 상태 → DB
              </Button>
              <p className="text-sm text-muted-foreground">
                모든 DB 상품에 대해 도매처 사이트 상태를 조회하고 DB에만 기록합니다.
              </p>
            </div>
            <div className="space-y-3 rounded-lg border border-border p-4">
              <Button
                variant="primary"
                onClick={() => runStatusSync('supplier_apply_state')}
                loading={runJob.isPending}
              >
                DB 도매처 상태 → 네이버
              </Button>
              <p className="text-sm text-muted-foreground">
                DB의 도매처 품절·재고 상태를 네이버 판매 및 전시 상태에 적용합니다.
              </p>
            </div>
          </div>

          {renderQueuedJobMessage('product_state_sync')}
          {renderJobError('product_state_sync')}
          {renderQueuedJobMessage('naver_pull_state')}
          {renderQueuedJobMessage('supplier_pull_state')}
          {renderQueuedJobMessage('supplier_apply_state')}
          {renderJobError('naver_pull_state')}
          {renderJobError('supplier_pull_state')}
          {renderJobError('supplier_apply_state')}
        </Card.Body>
      </Card>

      <Card>
        <Card.Header>
          <Card.Title>수동 실행</Card.Title>
        </Card.Header>
        <Card.Body className="space-y-4">
          <div className="grid gap-1">
            <label className="text-xs text-muted-foreground">실행할 작업</label>
            <Select
              value={manualJob}
              onValueChange={(value) => {
                setManualJobError(null);
                setManualJob(value as ManualJobType);
              }}
              options={JOB_OPTIONS.map(({ value, label }) => ({ value, label }))}
              className="w-full sm:max-w-sm"
            />
            <p className="text-xs text-muted-foreground">{selectedJob.description}</p>
          </div>

          {renderManualPayload()}

          <div className="flex items-center gap-3">
            <Button variant="secondary" onClick={runManualJob} loading={runJob.isPending}>
              {selectedJob.label} 실행
            </Button>
          </div>

          {renderQueuedJobMessage(manualJob)}
          {manualJobError && <p className="text-sm text-destructive">{manualJobError}</p>}
          {renderJobError(manualJob)}

        </Card.Body>
      </Card>
    </div>
  );
}
